import * as qs from 'qs';
import { ITokens } from '../store';
import { BaseService } from './BaseService';

export interface LoginResponse {
  readonly tokens: ITokens;
}

export enum ProviderTypes {
  APPLE = 'apple',
  FACEBOOK = 'facebook',
  GOOGLE = 'google',
  AMAZON = 'amazon',
  SANS_IDP = 'sans',
}

interface CognitoTokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
}

const COGNITO_LOGIN_PROVIDERS: {
  [key in ProviderTypes]: string;
} = {
  apple: 'SignInWithApple',
  facebook: 'Facebook',
  google: 'Google',
  amazon: 'LoginWithAmazon',
  sans: 'auth-service-saml-idp',
};
export class CognitoService extends BaseService {

  constructor() {
    super({ configPath: 'cognito' });
  }

  async callIdpCommand(command:string, params: any) {
    const { region, clientId } = await this.config();

    const url = `https://cognito-idp.${region}.amazonaws.com/`;

    const headers = {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${command}`,
    };

    return this.request({
      url,
      method: 'POST',
      headers,
      data: {
        ClientId: clientId,
        ...params,
      },
    });
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    // @ts-ignore TODO
    const { AuthenticationResult: tokens } = await this.callIdpCommand('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    });

    return {
      tokens: {
        access: tokens.AccessToken,
        refresh: tokens.RefreshToken,
        id: tokens.IdToken,
        expiresIn: tokens.ExpiresIn,
      },
    };
  }

  // TODO stop using any here
  // @ts-ignore
  async register(email:string, password:string, optOut: boolean, userAttributes: object): Promise<any> {
    const params:any = {
      Username: email,
      Password: password,
    };

    if (userAttributes) {
      params.UserAttributes = Object.entries(userAttributes).map(([Name, Value]) => ({ Name, Value }));
    }

    // @ts-ignore TODO
    const { UserConfirmed: confirmed, UserSub: userId } = await this.callIdpCommand('SignUp', params);

    const tokens = await this.login(email, password);

    return {
      confirmed,
      userId,
      ...tokens,
    };
  }

  async oauthOrigin() {
    const { domain, oauthOrigin } = await this.config();
    return oauthOrigin ? oauthOrigin : `https://${domain}/oauth2`;
  }

  async oauthTokenUrl() {
    const oauthOrigin = await this.oauthOrigin();
    return `${oauthOrigin}/token`;
  }

  async oauthRefreshTokenUrl() {
    // TODO consider changing the calling interface here to be more consistent
    // or just stop using cognito directly
    const defaultOAuthTokenUrl = await this.oauthTokenUrl();
    const { oauthOrigin } = await this.config();
    return oauthOrigin ? `${oauthOrigin}/refresh` : defaultOAuthTokenUrl;
  }

  async oauthAuthorizeUrl() {
    const oauthOrigin = await this.oauthOrigin();
    return `${oauthOrigin}/authorize`;
  }

  async fetchTokens(data: any, tokenUrl?: string): Promise<ITokens> {
    const { clientId: client_id } = await this.config();

    const url = tokenUrl || await this.oauthTokenUrl();

    const tokens = <CognitoTokenResponse> await this.request({
      url,
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: {
        client_id,
        ...data,
      },
    });
    return { id: tokens.id_token, access: tokens.access_token, refresh: tokens.refresh_token };
  }

  async refresh(refresh_token: string) {
    const tokenUrl = await this.oauthRefreshTokenUrl();
    const tokens = await this.fetchTokens({
      grant_type: 'refresh_token',
      refresh_token,
    }, tokenUrl);
    return tokens;
  }

  tokensFromCode(code: string, redirect_uri: string) {
    return this.fetchTokens({
      code,
      grant_type: 'authorization_code',
      redirect_uri,
    });
  }

  // TODO stop using any here Promise<void> is not allowed by jsii for some reason
  async oauthProviderRedirectUrl(origin: string, provider: ProviderTypes, options: Record<string, unknown> = {}): Promise<any> {
    const {
      clientId: client_id,
      scope,
      response_type = 'CODE',
      redirectPath,
    } = await this.config();

    const identity_provider = COGNITO_LOGIN_PROVIDERS[provider];
    if (!identity_provider) throw `ProviderNotFound ${provider} not found`;

    const redirect_uri = `${origin}${redirectPath}`;

    const query = {
      client_id,
      scope,
      identity_provider,
      response_type,
      redirect_uri,
      ...options,
    };

    query.scope = query.scope.join(' ');

    const authorizeUrl = await this.oauthAuthorizeUrl();

    return `${authorizeUrl}?${qs.stringify(query)}`;
  }

  async oauthLogoutUrl(logout_uri?: string): Promise<any> {
    const {
      oauthOrigin,
      domain: domain_name,
      clientId: client_id,
      redirectPath,
    } = await this.config();

    const query = {
      client_id,
      logout_uri: logout_uri || redirectPath,
    };

    const logoutUrl = oauthOrigin ? `${oauthOrigin}/logout` : `https://${domain_name}/logout`;
    return `${logoutUrl}?${qs.stringify(query)}`;
  }


  async forgotPassword(username: string, reset_type:string) {
    return this.callIdpCommand('ForgotPassword', {
      Username: username,
      ClientMetadata: {
        reset_type,
      },
    });
  }


  async confirmForgotPassword(username:string, newPassword:string, confirmationCode:string) {
    return this.callIdpCommand('ConfirmForgotPassword', {
      Username: username,
      ConfirmationCode: confirmationCode,
      Password: newPassword,
    });
  }

  async changePassword(oldPassword:string, newPassword:string) {
    const tokenService = await this.api.tokens.freshTokens();
    const accessToken = tokenService.access;

    const params = {
      AccessToken: accessToken,
      PreviousPassword: oldPassword,
      ProposedPassword: newPassword,
    };

    return this.callIdpCommand('ChangePassword', params);
  }
}
